import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTournaments } from '../api/client';
import { useI18n } from '../i18n';
import { usePageTitle } from '../hooks/usePageTitle';
import { useLiveBouts } from '../hooks/useLiveBouts';
import { todayLocal } from '../utils/calendarDate';
import { sortByStart, tournamentPhase, type TournamentPhase } from '../utils/tournament';
import TournamentCard from '../components/tournament/TournamentCard';
import Skeleton from '../components/shared/Skeleton';
import EmptyState from '../components/shared/EmptyState';
import AsyncContent from '../components/shared/AsyncContent';
import { useLoad } from '../hooks/useLoad';
import { CalendarIcon } from '../components/shared/Icons';
import type { Tournament } from '../types';

export default function Home() {
  const { t } = useI18n();
  usePageTitle(t.nav.home);
  // #457 — a failed load showed "No tournaments" on venue Wi-Fi.
  // #665, F02 — useLoad holds the loading, the error and Retry.
  const [liveKey, setLiveKey] = useState(0);
  const { data: { todayEvents, upcoming, recent }, loading, error: loadFailed, reload } = useLoad(
    () => getTournaments().then(r => {
      const list = r.data as Tournament[];
      const today = todayLocal();
      const inPhase = (phase: TournamentPhase) => list.filter(t => tournamentPhase(t, today) === phase);
      return {
        todayEvents: sortByStart(inPhase('today'), 'asc'),
        upcoming: sortByStart(inPhase('upcoming'), 'asc').slice(0, 3),
        recent: sortByStart(inPhase('past'), 'desc').slice(0, 1),
      };
    }),
    [],
    {
      context: 'Home:tournaments',
      initial: { todayEvents: [] as Tournament[], upcoming: [] as Tournament[], recent: [] as Tournament[] },
      // The live read starts again at each answer, also a Retry with the same tournaments.
      onData: () => setLiveKey(k => k + 1),
    },
  );

  // The live (current) bouts of today's tournaments.
  const liveBouts = useLiveBouts(todayEvents, liveKey);

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div
        className="rounded-2xl overflow-hidden relative text-white select-none"
        style={{
          backgroundImage: 'url(/hero.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 25%',
          minHeight: '220px',
        }}>
        {/* Gradient overlay — heavy at bottom for text, light at top to show fighters */}
        <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/50 to-black/10 rounded-2xl" />

        {/* Content anchored to bottom-left */}
        <div className="relative flex flex-col justify-end p-6 md:p-10 min-h-[220px]">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-white shrink-0 shadow-lg overflow-hidden">
              <img src="/logo.webp" alt="LMF" className="w-full h-full rounded-full object-cover" />
            </div>
          </div>
          <h1 className="text-2xl md:text-4xl font-black leading-tight drop-shadow-lg text-white">
            {t.home.title}
          </h1>
          <p className="text-white/75 text-sm md:text-base mt-1 drop-shadow-sm">
            {t.home.subtitle}
          </p>
        </div>
      </div>

      {/* Today's events */}
      {todayEvents.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
              {t.home.today}
            </h2>
            <Link to="/tournaments" className="text-sm text-gray-500 hover:text-gray-900">{t.home.viewAll}</Link>
          </div>
          <div className="space-y-3">
            {todayEvents.map(tournament => (
              <TournamentCard key={tournament.id} tournament={tournament}
                liveBouts={liveBouts[tournament.id] ?? []} isToday />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming events */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-lg">{t.home.upcoming}</h2>
          <Link to="/tournaments" className="text-sm text-gray-500 hover:text-gray-900">{t.home.viewAll}</Link>
        </div>
        <AsyncContent
          loading={loading}
          error={!!loadFailed}
          onRetry={reload}
          isEmpty={upcoming.length === 0}
          skeleton={<Skeleton.List shape="tournament-card" count={2} />}
          empty={todayEvents.length === 0 ? (
            <EmptyState
              icon={<CalendarIcon className="w-12 h-12" />}
              title={t.common.noUpcoming}
              hint={t.common.empty.upcomingHint}
            />
          ) : (
            <EmptyState
              compact
              icon={<CalendarIcon className="w-8 h-8" />}
              title={t.common.noFurther}
            />
          )}
        >
          <div className="space-y-3">
            {upcoming.map(tournament => (
              <TournamentCard key={tournament.id} tournament={tournament} />
            ))}
          </div>
        </AsyncContent>
      </section>

      {/* Recent events */}
      {!loading && recent.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg">{t.home.recent}</h2>
            <Link to="/tournaments" className="text-sm text-gray-500 hover:text-gray-900">{t.home.viewAll}</Link>
          </div>
          <div className="space-y-3">
            {recent.map(tournament => (
              <TournamentCard key={tournament.id} tournament={tournament} compact />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
